'use client';

import { useEffect, useRef, useState } from 'react';
import { usePathname } from 'next/navigation';
import { trackEvent } from '@/lib/amplitude';
import { usePendingApprovals } from '@/lib/comix/use-pending-approvals';
import PendingApprovalCard from './PendingApprovalCard';

// One shared flag across every page this mounts on — once the user closes the
// popup anywhere it never shows again ("one time only", i.e. it only ever gets a
// chance to appear once, on whichever /comix/* page they happen to load first).
const SEEN_KEY = 'comix_approval_popup_seen';

function alreadySeen(): boolean {
  try {
    return localStorage.getItem(SEEN_KEY) === '1';
  } catch {
    return true; // storage blocked → don't nag
  }
}
function markSeen() {
  try {
    localStorage.setItem(SEEN_KEY, '1');
  } catch {
    /* ignore */
  }
}

/**
 * One-time popup for printed comic books awaiting approval — same card as the
 * home-page banner, with a dismiss X. Mounted globally in comix/layout.tsx, so it's
 * live on every /comix/* page; bails out on the homepage itself (`/comix`), which
 * has the always-visible PendingApprovalBanner instead. Shows at most once ever,
 * on whichever page happens to be the first one the user lands on after purchase.
 */
export default function PendingApprovalPopup() {
  const pathname = usePathname();
  const { books } = usePendingApprovals();
  const [open, setOpen] = useState(false);
  const trackedRef = useRef(false);

  useEffect(() => {
    if (books.length > 0 && !alreadySeen()) setOpen(true);
  }, [books.length]);

  useEffect(() => {
    if (open && !trackedRef.current) {
      trackedRef.current = true;
      trackEvent('Approval Popup Shown', { page: pathname, count: books.length });
    }
  }, [open, pathname, books.length]);

  // Books all approved / expired while it's open → close it.
  useEffect(() => {
    if (open && books.length === 0) setOpen(false);
  }, [open, books.length]);

  // The homepage has its own always-visible banner (PendingApprovalBanner) —
  // never double up with this popup there too.
  if (pathname === '/comix') return null;
  if (!open || books.length === 0) return null;

  const close = () => {
    markSeen();
    setOpen(false);
    trackEvent('Approval Popup Dismissed', { page: pathname });
  };

  return (
    // items-center on every breakpoint (was items-end on mobile, a bottom-sheet
    // pin that let a tall popup run off the top of the screen instead of being
    // centered) — plus max-h-[90vh] + overflow-y-auto on the panel itself as a
    // safety net so a tall popup scrolls internally rather than overflowing the
    // viewport.
    <div className="fixed inset-0 z-[70] flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={close} />
      {/* sm:max-w-[720px] (was 640px) — gives the compact desktop row (see
          PendingApprovalCard's isCompact) enough width for its text column,
          now that the cover has a fixed 260px height plus 40px padding on each side. */}
      <div className="relative z-[1] w-full max-w-[420px] px-4 sm:max-w-[720px] sm:px-0">
        <button
          type="button"
          onClick={close}
          aria-label="Close"
          className="absolute -top-2 right-2 z-10 flex h-9 w-9 items-center justify-center rounded-full bg-white text-gray-500 shadow-md transition-colors hover:bg-gray-100 sm:-right-2 sm:-top-3"
        >
          <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
        {/* Plain white panel — no tint, and PendingApprovalCard skips its own
            elevated white "card" shell in a popup (isCompact) too, so there's just
            one white surface here, not a card floating on a tinted one.
            shadow-2xl lives on THIS outer div, with no overflow rule of its own —
            overflow-y-auto on the same element as a shadow clips the shadow at the
            scroll boundary, which showed up as a flat gray line across the bottom
            (that's what this was). The inner div does the actual scrolling/clipping,
            with a matching border-radius so content corners still round off. */}
        <div className="rounded-[22px] bg-white shadow-2xl">
          <div className="max-h-[90vh] overflow-y-auto rounded-[22px] p-4 sm:p-5">
            <PendingApprovalCard books={books} source="popup" />
          </div>
        </div>
      </div>
    </div>
  );
}
